import { Injectable } from '@angular/core';
import { Feature } from 'ol';
import { ParentLayers } from '../menu/view-menu/parent-layer';
import { TopLevelLayers } from '../menu/view-menu/top-level-layers';
import { CoordinatePointModel } from '../models/coordinate-point';
import { Segment } from '../models/Segment';
import * as olProj from 'ol/proj';
import { Route } from '../models/Route';
import { Layer, VectorLayerDMTR } from 'src/app/menu/view-menu/layer';
import VectorSource from 'ol/source/Vector';
import { Stroke, Style } from 'ol/style';
import { MapService } from './map.service';
import { ModalConfirmationService } from './modal-confirmation.service';
import { UtilsService } from './utils.service';
import Geometry from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import { features } from 'process';
import { FeatureType } from '../models/EnumTypes';
import { RouteSegmentsCsvModel } from '../models/routeSegmentsCSV';
import { RouteSegmentsXMLModel } from '../models/routeSegmentsXMLModel';
import { CoordenateService } from './coordenate.service';
import { Routesegment} from '../models/routesegment';
import { HttpClient } from '@angular/common/http';
import { EndpointsService } from './endpoints.service';
import { ModalControlService } from './modal-control.service';
import { UiBlockService } from './ui-block.service';
import { UserSettingsService } from './userSettings.service';
import { LayerVisibility } from '../models/UserSettings';



@Injectable({
  providedIn: 'root'
})
export class RouteSegmentsService {

  routeSegmentLayers: ParentLayers;
  isCreateProcedureFromRouteSegment: boolean = false;

  public model : RouteSegmentsCsvModel;
  public modelXML: RouteSegmentsXMLModel;

  constructor(private mapService : MapService,
    private modalConfirmation: ModalConfirmationService,
    private utilsService : UtilsService,
    private rcsControl: CoordenateService,
    private http: HttpClient,
    private endpointService: EndpointsService,
    private modalService: ModalControlService,
    private uiBlock: UiBlockService,
    private userSettingsService: UserSettingsService

    ) {
  }

  getRouteSegmentLayer(): ParentLayers{
    return this.routeSegmentLayers;
  }

  setRouteSegmentLayer(routeSegmentLayers: ParentLayers) : void{
    this.routeSegmentLayers = routeSegmentLayers;
  }

  transformFeatureInSegment(arg0: Feature<import('ol/geom/Geometry').default>, activeRCS: string) : Segment{
    let toReturn : Segment = new Segment();
    toReturn.routeDesignation = arg0.get('designation');

    var coordsStartPoint = olProj.toLonLat(arg0.get('points')[0], activeRCS);
    let startCoordinatePoint : CoordinatePointModel = new CoordinatePointModel();
    startCoordinatePoint.name = arg0.get('startPointName');
    startCoordinatePoint.type="WAYPOINT";
    startCoordinatePoint.published = true;
    startCoordinatePoint.coordpointid = arg0.get('startPointName');
    startCoordinatePoint.latitude = coordsStartPoint[1];
    startCoordinatePoint.longitude = coordsStartPoint[0];
    toReturn.startpoint = startCoordinatePoint;

    var coordsEndPoint = olProj.toLonLat(arg0.get('points')[1], activeRCS);
    let endCoordinatePoint : CoordinatePointModel = new CoordinatePointModel();
    endCoordinatePoint.type="WAYPOINT";
    endCoordinatePoint.published = true;
    endCoordinatePoint.coordpointid = arg0.get('endPointName');
    endCoordinatePoint.name = arg0.get('endPointName');
    endCoordinatePoint.latitude = coordsEndPoint[1];
    endCoordinatePoint.longitude = coordsEndPoint[0];
    toReturn.endpoint = endCoordinatePoint;

    toReturn.lowerVerticalDistance = +arg0.get('lowerVerticalDistance') * 100;
    toReturn.lowerVerticalUnitsMeasurement = arg0.get('lowerVerticalUnitsMeasurement');

    toReturn.upperVerticalDistance = +arg0.get('upperVerticalDistance') * 100;
    toReturn.upperVerticalUnitsMeasurement = arg0.get('upperVerticalUnitsMeasurement');

    toReturn.sequenceNumber = arg0.get('featureId');

    return toReturn;
  }

  calculateRouteSegmentsContinuity(route : Route) {
		let segs : Segment[] = route.segments;
		let numStarts : number = 0;
		let startCoords : CoordinatePointModel[] = [];
		let endCoords : CoordinatePointModel[] = [];

    segs.forEach( seg => {
      startCoords.push(seg.startpoint);
			endCoords.push(seg.endpoint);
    })

		startCoords.forEach( point => {
      if (!this.checkIfCoordinatePointAlreadyExist(point, endCoords)){
        numStarts++;
      }
    })

		route.continuous = numStarts == 1;
	}

  checkIfCoordinatePointAlreadyExist(point : CoordinatePointModel, list : CoordinatePointModel[]) : boolean {
    let toReturn : boolean = false;
    list.forEach( elm => {
      if(point.coordpointid == elm.coordpointid
      && point.name == elm.name
      && point.latitude == elm.latitude
      && point.longitude == elm.longitude){
        toReturn = true;
      }
    });
    return toReturn;
  }

  transformRouteSegmentToBeSaved(modelToBeTransformed : RouteSegmentsCsvModel, projectID: number): Routesegment{
    const modelToBeSaved: Routesegment = new Routesegment();
    modelToBeSaved.id = null;
    modelToBeSaved.layername = modelToBeTransformed.layerName;
    modelToBeSaved.linecolor = modelToBeTransformed.lineColor;
    modelToBeSaved.linecolornullval = modelToBeTransformed.lineColorNullVal;
    modelToBeSaved.lineweight = modelToBeTransformed.lineWeight;
    modelToBeSaved.features = JSON.stringify(modelToBeTransformed.features);
    modelToBeSaved.projectId = projectID;
    return modelToBeSaved;

  }

  saveState(model : RouteSegmentsCsvModel, projectID: number) : void{
    this.http
        .post(
          this.endpointService.routeSegmentsUrl,
          this.transformRouteSegmentToBeSaved(model, projectID),
          this.endpointService.httpOptions
        )
        .subscribe(
          (routeSegment: Routesegment) => {
            if (routeSegment){
              const layers : Layer[] = this.createNewRouteSegmentLayer(routeSegment);
              this.addRouteSegmentToLayers(layers);
              this.userSettingsService.createNewVisibilityLayer('Route Segments',JSON.stringify(routeSegment.id));
              this.mapService.zoomToFeatures(model.features);
            } else {
              this.modalService.showMesssageModal(
                'Error importing',
                'There was an error saving the Route Segments. Please make sure you are importing the correct XML file.'
              );
              throw new Error("Error Saving Route Segment.");
            }
          },
          (error) => {
            if (error && error.status == 401) {
              this.modalService.showMesssageModal(
                'Error Importing',
                'Default Project is not editable.'
              )
              throw new Error("Error Saving Route Segment.");;
            }
            this.modalService.showMesssageModal(
              'Error importing',
              'There was an error saving the Route Segments. Please make sure you are importing the correct XML file.'
            );
            throw new Error("Error Saving Route Segment.");
          }
        )
  }

  loadState(projectid : number, isSwitchProject: boolean = false) : void{
    isSwitchProject? this.uiBlock.startBlock("Adding entities to map..."): false;
    this.getRouteSegmentLayer().childLayers.forEach( layer => {
      layer.hideAll();
      layer.layers.forEach(x => {x.olLayer.setVisible(false)});
    });
    this.getRouteSegmentLayer().childLayers = [];
    this.getAllRouteSegments().subscribe((routeSegments: Routesegment[]) =>{
      try {
        if (routeSegments && routeSegments.length > 0){
          for (const routeSegment of routeSegments){
            const layers : Layer[] = this.createNewRouteSegmentLayer(routeSegment);
            this.addRouteSegmentToLayers(layers);
            for(let layer of layers){
              let visibilityLayer:LayerVisibility = this.userSettingsService.
                                                    userSettings.routeSegmentVisibilities.find((elem)=>elem.id == JSON.stringify(routeSegment.id));
              if(visibilityLayer != null){
                layer.visible = visibilityLayer.visible;
                layer.olLayer.setVisible(visibilityLayer.visible);
              }
            }
          }
        }
      } finally {
        isSwitchProject? this.uiBlock.stopBlock(): false;
      }
    },
    () => {
      isSwitchProject? this.uiBlock.stopBlock(): false;
    })
  }

  getAllRouteSegments(){
    return this.http.get<Routesegment[]>(
      this.endpointService.routeSegmentsUrl,
      this.endpointService.httpOptions
    );
  }

  createNewRouteSegmentLayer(model : Routesegment) : Layer[] {
    let layer : Layer = this.createLayer(model.id, model.layername, model.linecolor, model.lineweight);
    let nullLayer : Layer = this.createLayer(model.id, model.layername, model.linecolornullval, model.lineweight);
    const features: Feature<Geometry>[] = JSON.parse(model.features);
    features.forEach((feat : any) => {
      let feature: Feature = this.transformInFeature(feat.values_);
      feature = this.reprojectRouteSegmentFeature(feature, this.mapService.getMap('map').getView().getProjection().getCode());
      if(!feature.get("isNullLine")){
        layer.olLayer.getSource().addFeature(feature);
      }else{
        nullLayer.olLayer.getSource().addFeature(feature);
      }
    })
    return [layer, nullLayer];
  }


  createLayer(id:number, layerName : string, color : string, lineWeight : number) : Layer{
    var layer : Layer =
      {id: id.toString(), name: layerName, image:'', visible:true, olLayer:new VectorLayerDMTR({
        source: new VectorSource(),
        style: new Style({
          stroke: new Stroke({ color: color, width: lineWeight }),
          zIndex:11
        }),
        styleSelected: new Style({
          stroke: new Stroke({ color: '#ffea00', width: lineWeight }),
          zIndex:11
        }),
      })
    };
    layer.olLayer.setZIndex(2);
    return layer;
  }

  addRouteSegmentToLayers(layers : Layer[]) : void {
    var topLayer: TopLevelLayers = new TopLevelLayers(
        true,
        layers[0].name,
        layers,
        this.mapService.getMap('map'),
        this.modalConfirmation,
        null,
        null,
        null,
        "RouteSegments"
      );
      topLayer.status = true;
      this.getRouteSegmentLayer().childLayers.push(topLayer);
  }


  transformInFeature(old : any) : Feature<Geometry>{
    let feature = new Feature({
      geometry: new LineString(old.points),
      layer: old.layer,
      featureId: old.featureId,
      selectedOnManage: false,
      featureType : FeatureType.RouteSegments,//route Segment,
      designation: old.designation,
      startPointName: old.startPointName,
      endPointName: old.endPointName,
      points: old.points,
      isNullLine: old.isNullLine,
      color: (old.lowerVerticalDistance != "NULL" && old.upperVerticalDistance != "NULL") ? old.lineColor : old.lineColorNullVal,
      lowerVerticalDistance: old.lowerVerticalDistance,
      lowerVerticalUnitsMeasurement: old.lowerVerticalUnitsMeasurement,
      upperVerticalDistance: old.upperVerticalDistance,
      upperVerticalUnitsMeasurement: old.upperVerticalUnitsMeasurement,
      pointsProjection: old.pointsProjection
    });
    return feature;
  }

  deleteRouteSegments(id : string) : void {
    this.http
      .delete<boolean>(
        this.endpointService.routeSegmentsUrl + '/' + id,
        this.endpointService.httpOptions
      )
      .subscribe(
        () => {},
        (error) => {
          if (error && error.status == 401) {
            this.modalService.showMesssageModal(
              'Error Deleting',
              'Default Project is not editable.'
            )
            return;
          }
          this.modalService.showMesssageModal(
            'Error deleting',
            'There was an error deleting the Route Segments.'
          );
        }
      );
  }

  reprojectRouteSegmentFeature(feature: Feature, targetProjection: string) {
    return new Feature({
      ...feature.getProperties(), // Preserve other properties of the feature
      geometry: feature.getGeometry().clone().transform(feature.get('pointsProjection'), targetProjection)
    });
  }


  createFeaturesFromFileContent() : Feature[] {
    var features : Feature[] = [];
    this.model.fileContent.sort((a, b) => (a.sequenceNumber > b.sequenceNumber) ? 1 : -1).forEach(line => {
      if(features.find(x => x.get("featureId") == line.sequenceNumber)){
        return;
      }
      let points = [];
      //first
      points.push([
        line.startSegmentPointLongitude,
        line.startSegmentPointLatitude,
      ]);
      points.push([
        line.endSegmentPointLongitude,
        line.endSegmentPointLatitude,
      ]);
      for (var i = 0; i < points.length; i++) {
        //TODO
        //points[i] = olProj.transform(points[i], this.rcsControl.activeRCS.isactive? this.rcsControl.activeRCS.epsg : 'EPSG:4326', this.mapService.getMaps().getView().getProjection());
        points[i] = olProj.transform(points[i], "EPSG:4326", this.mapService.getMaps().getView().getProjection());
      }
      let feat = new Feature({
        geometry: new LineString(points),
        layer: this.model.layerName,
        featureId: line.sequenceNumber,
        selectedOnManage: false,
        featureType : FeatureType.RouteSegments,//route Segment,
        designation: line.designation,
        startPointName: line.startPointName,
        endPointName: line.endPointName,
        points: points,
        isNullLine: (line.lowerVerticalDistance == "NULL" || line.upperVerticalDistance == "NULL"),
        color: (line.lowerVerticalDistance != "NULL" && line.upperVerticalDistance != "NULL") ? this.model.lineColor : this.model.lineColorNullVal,
        lowerVerticalDistance: line.lowerVerticalDistance,
        lowerVerticalUnitsMeasurement: line.lowerVerticalUnitsMeasurement,
        upperVerticalDistance: line.upperVerticalDistance,
        upperVerticalUnitsMeasurement: line.upperVerticalUnitsMeasurement,
        pointsProjection: this.mapService.getMaps().getView().getProjection().getCode()
      });

      features.push(feat);
    })
    return features;
  }

  createFeaturesFromXMLContent() : Feature[] {
    var features : Feature[] = [];
    this.modelXML.routeSegments.sort((a, b) => (a.sequenceNumber > b.sequenceNumber) ? 1 : -1).forEach(line => {
      if(features.find(x => x.get("featureId") == line.sequenceNumber)){
        return;
      }
      let points = [];
      //first
      points.push([
        line.startSegmentPointLongitude,
        line.startSegmentPointLatitude,
      ]);
      points.push([
        line.endSegmentPointLongitude,
        line.endSegmentPointLatitude,
      ]);
      for (var i = 0; i < points.length; i++) {
        //TODO
        //points[i] = olProj.transform(points[i], this.rcsControl.activeRCS.isactive? this.rcsControl.activeRCS.epsg : 'EPSG:4326', this.mapService.getMaps().getView().getProjection());
        points[i] = olProj.transform(points[i], "EPSG:4326", this.mapService.getMaps().getView().getProjection());
      }
      let feat = new Feature({
        geometry: new LineString(points),
        layer: this.modelXML.layerName,
        featureId: line.sequenceNumber,
        selectedOnManage: false,
        featureType : FeatureType.RouteSegments,//route Segment,
        designation: line.designation,
        startPointName: line.startPointName,
        endPointName: line.endPointName,
        points: points,
        isNullLine: (line.lowerVerticalDistanceValue == null || line.upperVerticalDistanceValue == null),
        color: (line.lowerVerticalDistanceValue != null && line.upperVerticalDistanceValue != null) ? this.modelXML.lineColor : this.modelXML.lineColorNullVal,
        lowerVerticalDistance: line.lowerVerticalDistanceValue,
        lowerVerticalUnitsMeasurement: line.lowerVerticalDistanceUnits,
        upperVerticalDistance: line.upperVerticalDistanceValue,
        upperVerticalUnitsMeasurement: line.upperVerticalDistanceUnits
      });

      features.push(feat);
    })
    return features;
  }
}
